// src/hooks/useCollabDots.js
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

/**
 * Syncs dots via a single Y.Map('dots') with one key: 'list'
 * Dot shape: { id: string, position: {x:number, y:number}, radius:number, color:string }
 */
export default function useCollabDots(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yMapRef = useRef(null);

  const cbRef = useRef(onRemoteUpdate);
  useEffect(() => {
    cbRef.current = onRemoteUpdate;
  }, [onRemoteUpdate]);

  // ignore our own echoes
  const lastSentHashRef = useRef("");

  const stableSort = (arr) =>
    [...arr].sort((a, b) => {
      if (a.id !== b.id) return String(a.id).localeCompare(String(b.id));
      const ax = a.position?.x ?? 0,
        ay = a.position?.y ?? 0;
      const bx = b.position?.x ?? 0,
        by = b.position?.y ?? 0;
      if (ax !== bx) return ax - bx;
      if (ay !== by) return ay - by;
      const ar = Number.isFinite(a.radius) ? a.radius : 5;
      const br = Number.isFinite(b.radius) ? b.radius : 5;
      if (ar !== br) return ar - br;
      return String(a.color || "").localeCompare(String(b.color || ""));
    });

  const normalize = (arr) =>
    stableSort(
      (Array.isArray(arr) ? arr : []).map((d) => ({
        id: String(d.id || ""),
        position: {
          x: Number.isFinite(d?.position?.x) ? Number(d.position.x) : 0,
          y: Number.isFinite(d?.position?.y) ? Number(d.position.y) : 0,
        },
        radius: Number.isFinite(d.radius) ? Number(d.radius) : 5,
        color: typeof d.color === "string" ? d.color : "#00E5FF",
      }))
    );

  const stableHash = (obj) => {
    try {
      return JSON.stringify(obj);
    } catch {
      return String(Date.now());
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();

    // clean socket URL (avoid trailing slashes or quotes)
    const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
    const socketUrl = raw.replace(/['\"\s]+/g, "").replace(/\/+$/g, "");
    const room = String(projectId).trim();

    const provider = new WebsocketProvider(socketUrl, room, ydoc);
    const yMap = ydoc.getMap("dots"); // Y.Map, single key 'list'

    const emit = () => {
      const list = yMap.get("list") || [];
      const safe = normalize(list);
      const h = stableHash(safe);
      if (h === lastSentHashRef.current) return; // ignore our own write
      cbRef.current?.(safe);
    };

    const observer = () => emit();

    yMap.observe(observer);
    emit(); // initial hydrate

    provider.on("status", () => {
      /* optional debug */
    });

    ydocRef.current = ydoc;
    providerRef.current = provider;
    yMapRef.current = yMap;

    return () => {
      try {
        yMap.unobserve(observer);
      } catch {}
      try {
        provider.destroy();
      } catch {}
      try {
        ydoc.destroy();
      } catch {}
      ydocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
    };
  }, [projectId]);

  // Atomic replace of whole list
  const syncDots = (nextArr = []) => {
    const yMap = yMapRef.current;
    const ydoc = ydocRef.current;
    if (!yMap || !ydoc) return;

    const normalized = normalize(nextArr);
    const h = stableHash(normalized);

    ydoc.transact(() => {
      yMap.set("list", normalized);
    });

    lastSentHashRef.current = h; // mark our own write to ignore echo
  };

  return { syncDots };
}
